import { Button } from "@nwga/ui/components/button";
import { Field } from "@nwga/ui/components/field";
import { Input } from "@nwga/ui/components/input";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MailCheckIcon } from "lucide-react";
import * as React from "react";
import { AuthCard } from "../components/auth-card.tsx";
import { captchaHeaders, useTurnstile } from "../components/turnstile.tsx";
import { env } from "../env.ts";
import { authClient } from "../lib/auth.ts";

export const Route = createFileRoute("/forgot-password")({
	component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
	const [sent, setSent] = React.useState(false);
	const [pending, setPending] = React.useState(false);
	const turnstile = useTurnstile();

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setPending(true);

		const data = new FormData(event.currentTarget);
		await authClient.requestPasswordReset({
			email: String(data.get("email")),
			redirectTo: `${env.webUrl}/reset-password`,
			fetchOptions: { headers: captchaHeaders(turnstile.token) },
		});

		setPending(false);
		// Shown whatever happened, including when the address has no account.
		// Branching on the result here would turn this form into an oracle for
		// "is this person registered?" — which is exactly what it must not be.
		setSent(true);
	};

	if (sent) {
		return (
			<AuthCard
				title="Check your email"
				description="If there's an account with that address, we've sent a link to reset its password. It expires in one hour."
				footer={
					<>
						Didn't get it? Check your spam folder, or{" "}
						<button
							type="button"
							onClick={() => setSent(false)}
							className="font-medium text-foreground underline underline-offset-4"
						>
							try another address
						</button>
						.
					</>
				}
			>
				<div className="flex items-center gap-3 rounded-lg border border-dashed p-4">
					<MailCheckIcon
						className="size-5 shrink-0 text-muted-foreground"
						aria-hidden="true"
					/>
					<p className="text-muted-foreground text-sm">
						Follow the link in the email to choose a new password. Your current
						password keeps working until you do.
					</p>
				</div>
			</AuthCard>
		);
	}

	return (
		<AuthCard
			title="Reset your password"
			description="We'll email you a link to choose a new one."
			footer={
				<>
					Remembered it?{" "}
					<Link
						to="/sign-in"
						className="font-medium text-foreground underline-offset-4 hover:underline"
					>
						Sign in
					</Link>
				</>
			}
		>
			<form
				className="space-y-4"
				onSubmit={(event) => void handleSubmit(event)}
			>
				<Field label="Email" required>
					{(props) => (
						<Input
							{...props}
							name="email"
							type="email"
							autoComplete="email"
							required
							autoFocus
						/>
					)}
				</Field>

				{turnstile.widget}

				<Button
					type="submit"
					className="w-full"
					disabled={pending || turnstile.pending}
				>
					{pending ? "Sending…" : "Send the link"}
				</Button>
			</form>
		</AuthCard>
	);
}
